import { useState } from 'react';
import { collection, addDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { UserSearchAutocomplete } from '../UserSearchAutocomplete';
import { UserPlus, Loader2, CheckCircle2, X } from 'lucide-react';

interface User {
  id: string;
  email: string;
  name?: string;
  customerId?: string;
  createdAt: Date;
  createdVia?: 'self' | 'admin_proxy';
  pendingVerification?: boolean;
}

interface VoteOnBehalfButtonProps {
  customerId: string;
  requestId: string;
  requestTitle: string;
  brandColor: string;
  onSuccess?: () => void;
}

export default function VoteOnBehalfButton({
  customerId,
  requestId,
  requestTitle,
  brandColor,
  onSuccess,
}: VoteOnBehalfButtonProps) {
  const { user, userProfile } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleUserSelect = async (selectedUser: User) => {
    setError(null);

    // Check if user has already voted
    try {
      const votesRef = collection(db, 'customers', customerId, 'requests', requestId, 'votes');
      const q = query(votesRef, where('userId', '==', selectedUser.id));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        setError('This user has already voted on this request.');
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Show confirmation dialog
      setSelectedUser(selectedUser);
      setShowDropdown(false);
      setShowConfirmation(true);
    } catch (err) {
      console.error('Error checking votes:', err);
      setError('Failed to check vote status. Please try again.');
    }
  };

  const handleCreateNewUser = () => {
    // For now, just show an error. In a full implementation, this would open a create user form
    setError('Please create the user first using "Submit on Behalf" feature.');
    setTimeout(() => setError(null), 3000);
  };

  const handleConfirmVote = async () => {
    if (!user || !userProfile || !selectedUser) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Create vote document
      await addDoc(
        collection(db, 'customers', customerId, 'requests', requestId, 'votes'),
        {
          userId: selectedUser.id,
          userEmail: selectedUser.email,
          userNickname: selectedUser.name || '',
          customerId: customerId,
          requestId: requestId,
          votedBy: {
            adminId: user.uid,
            adminName: userProfile.nickname || user.email,
            onBehalfOf: selectedUser.id,
            timestamp: Timestamp.now(),
          },
          createdAt: Timestamp.now(),
        }
      );

      // TODO: Send email notification to user
      console.log('Vote cast on behalf of:', selectedUser.email);

      setSuccess(true);

      setTimeout(() => {
        setShowConfirmation(false);
        setSelectedUser(null);
        setSuccess(false);
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (err) {
      console.error('Error casting vote:', err);
      setError('Failed to cast vote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setSelectedUser(null);
    setError(null);
  };

  return (
    <div className="relative">
      <Button
        onClick={() => setShowDropdown(!showDropdown)}
        variant="outline"
        size="sm"
        className="gap-2"
      >
        <UserPlus className="h-4 w-4" />
        Vote on Behalf
      </Button>

      {/* Dropdown for user search - rendered as fixed overlay */}
      {showDropdown && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20" onClick={() => setShowDropdown(false)}>
          <div
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 w-96 max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Cast vote for:</p>
                <button
                  onClick={() => setShowDropdown(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <UserSearchAutocomplete
                customerId={customerId}
                onUserSelect={handleUserSelect}
                onCreateNew={handleCreateNewUser}
                placeholder="Search by email..."
              />
            </div>

            {error && (
              <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl">
            {!success ? (
              <>
                <h3 className="text-lg font-semibold mb-4">Confirm Vote</h3>

                <div className="space-y-3 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">Cast vote on behalf of:</p>
                    <p className="font-medium">{selectedUser.email}</p>
                    {selectedUser.name && (
                      <p className="text-sm text-gray-500">{selectedUser.name}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">For request:</p>
                    <p className="font-medium">{requestTitle}</p>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      ✉️ User will be notified via email
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCancelConfirmation}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmVote}
                    disabled={isSubmitting}
                    style={{ backgroundColor: brandColor }}
                    className="text-white"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Casting Vote...
                      </>
                    ) : (
                      'Cast Vote'
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                  Vote cast successfully!
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {selectedUser.email} will be notified
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
